import { useState } from "react";
import jsPDF from "jspdf";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Download, Save, Sparkles } from "lucide-react";

const emptyResume = {
  name: "", email: "", phone: "", location: "",
  target_role: "",
  objective: "",
  education: [{ school: "", grade: "", start: "", end: "" }],
  skills: [""],
  activities: [{ title: "", org: "", details: "" }],
  awards: [""],
  experience: [{ role: "", company: "", start: "", end: "", details: "" }],
  volunteer: [{ role: "", org: "", details: "" }],
  references: "References available upon request.",
};

export default function ResumeBuilder() {
  const { user, refresh } = useAuth();
  const [r, setR] = useState({
    ...emptyResume,
    name: user?.name || "",
    email: user?.email || "",
    location: user?.location || "",
    ...(user?.resume || {}),
  });
  const [generating, setGenerating] = useState(false);

  const set = (path, val) => {
    const next = { ...r };
    const parts = path.split(".");
    let ref = next;
    for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
    ref[parts[parts.length - 1]] = val;
    setR(next);
  };

  const addRow = (key, blank) => setR({ ...r, [key]: [...(r[key]||[]), blank] });
  const remRow = (key, idx) => setR({ ...r, [key]: r[key].filter((_,i)=>i!==idx) });

  const generateObjective = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/objective", {
        name: r.name, age: user?.age, education_level: user?.education_level,
        skills: (r.skills || []).filter(Boolean), interests: user?.interests || [],
        target_role: r.target_role,
      });
      set("objective", data.objective);
      toast.success("Objective generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const saveToProfile = async () => {
    try {
      await api.put("/profile", { resume: r });
      await refresh();
      toast.success("Resume saved to profile");
    } catch(e){ toast.error(formatApiError(e)); }
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    let y = 56;
    const M = 56;
    const W = 612 - M * 2;
    doc.setFont("helvetica", "bold"); doc.setFontSize(22);
    doc.text(r.name || "Your Name", M, y); y += 22;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const contact = [r.email, r.phone, r.location].filter(Boolean).join(" · ");
    doc.text(contact, M, y); y += 18;

    const section = (title) => {
      y += 8;
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(title.toUpperCase(), M, y);
      doc.setLineWidth(1); doc.line(M, y+3, M+W, y+3);
      y += 16;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    };
    const line = (t) => {
      if (!t) return;
      const wrapped = doc.splitTextToSize(t, W);
      wrapped.forEach((w) => { if (y > 740) { doc.addPage(); y = 56; } doc.text(w, M, y); y += 13; });
    };

    if (r.objective) { section("Objective"); line(r.objective); }
    if ((r.education||[]).some(e=>e.school)) { section("Education"); r.education.forEach(e => e.school && line(`${e.school} — ${e.grade} (${e.start||""}${e.end?"–"+e.end:""})`)); }
    if ((r.skills||[]).filter(Boolean).length) { section("Skills"); line(r.skills.filter(Boolean).join(" · ")); }
    if ((r.experience||[]).some(e=>e.role)) { section("Work Experience"); r.experience.forEach(e => { if(!e.role) return; line(`${e.role} — ${e.company} (${e.start||""}${e.end?"–"+e.end:""})`); if(e.details) line(`  • ${e.details}`); }); }
    if ((r.volunteer||[]).some(e=>e.role)) { section("Volunteer Experience"); r.volunteer.forEach(e => { if(!e.role) return; line(`${e.role} — ${e.org}`); if(e.details) line(`  • ${e.details}`); }); }
    if ((r.activities||[]).some(e=>e.title)) { section("School Activities / Clubs / Sports"); r.activities.forEach(e => { if(!e.title) return; line(`${e.title} — ${e.org}`); if(e.details) line(`  • ${e.details}`); }); }
    if ((r.awards||[]).filter(Boolean).length) { section("Awards"); r.awards.filter(Boolean).forEach(a=>line(`• ${a}`)); }
    section("References"); line(r.references || "References available upon request.");

    doc.save(`${(r.name||"resume").replace(/\s+/g, "_")}-resume.pdf`);
  };

  return (
    <div className="nh-container py-10">
      <Toaster position="top-center"/>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl">Resume Builder</h1>
          <p className="text-[var(--nh-muted)] mt-1">Youth-friendly. No experience? List your school, clubs, sports, and volunteer work.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={saveToProfile} className="nh-btn nh-btn-ghost text-xs" data-testid="resume-save"><Save size={14}/> Save to profile</button>
          <button onClick={downloadPDF} className="nh-btn nh-btn-primary text-xs" data-testid="resume-download"><Download size={14}/> Download PDF</button>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Contact */}
        <section className="nh-card p-5">
          <h2 className="font-display font-black text-xl">Contact</h2>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <input placeholder="Full Name" className="nh-input" value={r.name} onChange={(e)=>set("name", e.target.value)} data-testid="rb-name"/>
            <input placeholder="Email" className="nh-input" value={r.email} onChange={(e)=>set("email", e.target.value)} data-testid="rb-email"/>
            <input placeholder="Phone" className="nh-input" value={r.phone} onChange={(e)=>set("phone", e.target.value)} data-testid="rb-phone"/>
            <input placeholder="Location" className="nh-input" value={r.location} onChange={(e)=>set("location", e.target.value)} data-testid="rb-location"/>
          </div>
        </section>

        {/* Objective */}
        <section className="nh-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-black text-xl">Objective</h2>
            <button onClick={generateObjective} disabled={generating} className="nh-btn nh-btn-secondary text-xs" data-testid="rb-generate-objective">
              <Sparkles size={14}/> {generating ? "Thinking…" : "AI Generate"}
            </button>
          </div>
          <input placeholder="Target role (e.g., Barista, Camp counselor)" className="nh-input mt-3" value={r.target_role} onChange={(e)=>set("target_role", e.target.value)} data-testid="rb-target-role"/>
          <textarea rows={4} placeholder="Your objective statement" className="nh-input mt-3" value={r.objective} onChange={(e)=>set("objective", e.target.value)} data-testid="rb-objective"/>
        </section>

        {/* Education */}
        <section className="nh-card p-5 md:col-span-2">
          <h2 className="font-display font-black text-xl">Education</h2>
          {r.education.map((e, i)=>(
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              <input placeholder="School" className="nh-input" value={e.school} onChange={(ev)=>set(`education.${i}.school`, ev.target.value)}/>
              <input placeholder="Grade / Degree" className="nh-input" value={e.grade} onChange={(ev)=>set(`education.${i}.grade`, ev.target.value)}/>
              <input placeholder="Start" className="nh-input" value={e.start} onChange={(ev)=>set(`education.${i}.start`, ev.target.value)}/>
              <input placeholder="End" className="nh-input" value={e.end} onChange={(ev)=>set(`education.${i}.end`, ev.target.value)}/>
            </div>
          ))}
          <button className="nh-btn nh-btn-ghost text-xs mt-3" onClick={()=>addRow("education", { school:"", grade:"", start:"", end:"" })}>+ Add education</button>
        </section>

        {/* Skills */}
        <section className="nh-card p-5">
          <h2 className="font-display font-black text-xl">Skills</h2>
          {(r.skills||[]).map((s, i)=>(
            <div key={i} className="flex gap-2 mt-2">
              <input placeholder="e.g. Customer service" className="nh-input" value={s} onChange={(e)=>{ const n=[...r.skills]; n[i]=e.target.value; setR({...r, skills:n}); }}/>
              <button className="nh-btn nh-btn-ghost text-xs" onClick={()=>remRow("skills", i)}>×</button>
            </div>
          ))}
          <button className="nh-btn nh-btn-ghost text-xs mt-3" onClick={()=>addRow("skills", "")}>+ Add skill</button>
        </section>

        {/* Awards */}
        <section className="nh-card p-5">
          <h2 className="font-display font-black text-xl">Awards</h2>
          {(r.awards||[]).map((s, i)=>(
            <div key={i} className="flex gap-2 mt-2">
              <input placeholder="e.g. Honor Roll 2024" className="nh-input" value={s} onChange={(e)=>{ const n=[...r.awards]; n[i]=e.target.value; setR({...r, awards:n}); }}/>
              <button className="nh-btn nh-btn-ghost text-xs" onClick={()=>remRow("awards", i)}>×</button>
            </div>
          ))}
          <button className="nh-btn nh-btn-ghost text-xs mt-3" onClick={()=>addRow("awards", "")}>+ Add award</button>
        </section>

        {/* Experience */}
        <section className="nh-card p-5 md:col-span-2">
          <h2 className="font-display font-black text-xl">Work Experience</h2>
          {r.experience.map((e, i)=>(
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              <input placeholder="Role" className="nh-input" value={e.role} onChange={(ev)=>set(`experience.${i}.role`, ev.target.value)}/>
              <input placeholder="Company" className="nh-input" value={e.company} onChange={(ev)=>set(`experience.${i}.company`, ev.target.value)}/>
              <input placeholder="Start" className="nh-input" value={e.start} onChange={(ev)=>set(`experience.${i}.start`, ev.target.value)}/>
              <input placeholder="End" className="nh-input" value={e.end} onChange={(ev)=>set(`experience.${i}.end`, ev.target.value)}/>
              <textarea placeholder="Highlights" rows={2} className="nh-input md:col-span-4" value={e.details} onChange={(ev)=>set(`experience.${i}.details`, ev.target.value)}/>
            </div>
          ))}
          <button className="nh-btn nh-btn-ghost text-xs mt-3" onClick={()=>addRow("experience", { role:"", company:"", start:"", end:"", details:"" })}>+ Add experience</button>
        </section>

        {/* Volunteer */}
        <section className="nh-card p-5">
          <h2 className="font-display font-black text-xl">Volunteer</h2>
          {r.volunteer.map((e, i)=>(
            <div key={i} className="grid grid-cols-2 gap-2 mt-3">
              <input placeholder="Role" className="nh-input" value={e.role} onChange={(ev)=>set(`volunteer.${i}.role`, ev.target.value)}/>
              <input placeholder="Organization" className="nh-input" value={e.org} onChange={(ev)=>set(`volunteer.${i}.org`, ev.target.value)}/>
              <textarea placeholder="Impact" rows={2} className="nh-input col-span-2" value={e.details} onChange={(ev)=>set(`volunteer.${i}.details`, ev.target.value)}/>
            </div>
          ))}
          <button className="nh-btn nh-btn-ghost text-xs mt-3" onClick={()=>addRow("volunteer", { role:"", org:"", details:"" })}>+ Add volunteer</button>
        </section>

        {/* Activities */}
        <section className="nh-card p-5">
          <h2 className="font-display font-black text-xl">Activities / Sports / Clubs</h2>
          {r.activities.map((e, i)=>(
            <div key={i} className="grid grid-cols-2 gap-2 mt-3">
              <input placeholder="Activity" className="nh-input" value={e.title} onChange={(ev)=>set(`activities.${i}.title`, ev.target.value)}/>
              <input placeholder="Group/Team" className="nh-input" value={e.org} onChange={(ev)=>set(`activities.${i}.org`, ev.target.value)}/>
              <textarea placeholder="Role, wins, impact" rows={2} className="nh-input col-span-2" value={e.details} onChange={(ev)=>set(`activities.${i}.details`, ev.target.value)}/>
            </div>
          ))}
          <button className="nh-btn nh-btn-ghost text-xs mt-3" onClick={()=>addRow("activities", { title:"", org:"", details:"" })}>+ Add activity</button>
        </section>
      </div>

      <p className="text-xs text-[var(--nh-muted)] mt-6">Tip: References available upon request is a fine placeholder — you don't need real references listed here.</p>
    </div>
  );
}
